<template>
  <div class="care-banner-slider">
    <div class="care-banner-slider__main">
      <video
        class="care-banner-slider__bg"
        :src="
          isMobile
            ? '/media/video/care/section-1-m.mp4'
            : '/media/video/care/section-1.mp4'
        "
        muted
        playsinline
        autoplay
        loop
        @loadedmetadata="isVideoLoaded = true"
        @timeupdate="onTimeupdate"
      />
      <div
        v-if="!isVideoLoaded"
        class="care-banner-slider__bg"
        :style="{
          backgroundImage: isMobile
            ? 'url(/media/img/products/care/section-1-preview-m.jpg)'
            : 'url(/media/img/products/care/section-1-preview.jpg)'
        }"
      />
      <div class="care-banner-slider__container container">
        <div class="care-banner-slider__content">
          <div class="care-banner-slider__logo">
            <img
              :src="
                isMobile
                  ? '/media/img/products/care/logo-m.svg'
                  : '/media/img/products/care/logo.svg'
              "
              alt="logo"
            />
          </div>
          <transition name="care-banner-slide" mode="out-in">
            <template v-for="(slide, i) in slidesData">
              <div
                v-if="slideIndex === i"
                :key="i"
                class="care-banner-slider__slide"
              >
                <div class="care-banner-slider__title">
                  {{ slide.title }}
                </div>
                <div class="care-banner-slider__description">
                  {{ slide.description }}
                </div>
              </div>
            </template>
          </transition>
          <a
            class="care-banner-slider__button btn btn-learn-more"
            href="https://my.lollipop.camera/subscription2"
            target="_blank"
          >
            {{ $t("care.banner.btnText") }}
          </a>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';

export default {
  name: 'CareBannerSlider',
  data() {
    return {
      isVideoLoaded: false,
      slideIndex: 0,
    };
  },
  computed: {
    ...mapState({
      isMobile: (state) => state.isMobile,
      languageCode: (state) => state.languageCode,
    }),
    slidesData() {
      return [
        {
          title: this.$t('care.sliderCare.title'),
          description: this.$t('care.sliderCare.desc'),
        },
        {
          title: this.$t('care.sliderBreathing.title'),
          description: this.$t('care.sliderBreathing.desc'),
        },
        {
          title: this.$t('care.sliderSleep.title'),
          description: this.$t('care.sliderSleep.desc'),
        },
        {
          title: this.$t('care.sliderSnapCute.title'),
          description: this.$t('care.sliderSnapCute.desc'),
        },
      ];
    },
  },
  methods: {
    onTimeupdate(e) {
      const time = e.target.currentTime;
      if (time > 1.5 && time < 3.5) {
        if (this.slideIndex === 0) {
          this.slideIndex = 1;
          return;
        }
      }
      this.slideIndex = (Math.floor((time + 1.5) / 3.5) + this.slidesData.length)
        % this.slidesData.length;
    },
  },
};
</script>

<style lang="sass" scoped>
.container
  width: 100%;
  padding: 0 9vw;

  @media screen and (max-width: 1440px)
    padding: 0 3vw;

  @media screen and (max-width: 414px)
    padding: 0 30px;

  @media screen and (max-width: 375px)
    padding: 0 25px;

.care-banner-slide
  &-enter
    .care-banner-slider
      &__title, &__description
        opacity: 0
        transform: translateX(20%)

    &-active
      transition: opacity 1.2s, transform 1.2s

      .care-banner-slider
          &__title, &__description
            transition: opacity 1.2s, transform 1.2s
          &__title
            transition-delay: 0.15s

  &-leave
    &-to
      .care-banner-slider
        &__title, &__description
          opacity: 0
          transform: translateX(-20%)

    &-active
      transition: opacity 0.8s, transform 0.8s

      .care-banner-slider
          &__title, &__description
            transition: opacity 0.8s, transform 0.8s
          &__title
            transition-delay: 0.1s

.care-banner-slider
  position: relative
  width: 100%
  height: calc(100svh - 72px - 46px)
  overflow: hidden

  @media screen and (orientation: portrait), (max-width: 768px)
    height: calc(100svh - 56px - 46px)

  @media screen and (max-width: 480px)
    height: calc(100svh - 56px - 68px)

  > *
    position: absolute
    top: 0
    left: 0
    width: 100%
    height: 100%

  &__main
    video
      object-fit: cover

  &__bg
    position: absolute
    top: 0
    left: 0
    width: 100%
    height: 100%
    background: #fff no-repeat center / cover
    pointer-events: none

  &__container
    width: 100%
    height: 100%

  &__content
    position: relative
    max-width: 490px
    top: 50%
    transform: translateY(-50%)
    z-index: 1
    margin-left: auto

    @media screen and (orientation: portrait), (max-width: 768px)
      max-width: none
      top: auto
      transform: none
      width: 100%
      height: 100%
      display: flex;
      flex-direction: column;

  &__logo
    margin-bottom: 35px

    @media screen and (orientation: portrait), (max-width: 768px)
      flex: 1 1 auto;
      margin-top: 40px

  &__slide
    position: relative
    margin-bottom: 120px

    @media screen and (orientation: portrait), (max-width: 768px)
      margin-bottom: 40px

    @media screen and (orientation: portrait), (max-width: 375px)
      margin-bottom: 20px

  &__title
    color: #fff
    font-size: 48px
    margin-bottom: 12px
    font-weight: 700

    @media screen and (max-width: 1440px)
      font-size: 36px

    @media screen and (max-width: 1024px)
      font-size: 32px

    @media screen and (orientation: portrait), (max-width: 768px)
      font-size: 24px
      margin-bottom: 16px

    @media screen and (orientation: portrait), (max-width: 375px)
      margin-bottom: 8px

  &__description
    font-size: 32px
    line-height: 1.25

    @media screen and (max-width: 1440px)
      font-size: 24px

    @media screen and (max-width: 1024px)
      font-size: 20px

    @media screen and (orientation: portrait), (max-width: 768px)
      font-size: 16px
      line-height: 1.4

  &__button

    @media screen and (max-width: 768px)
      margin: 0 auto 50px

    @media screen and (max-width: 414px)
      margin: 0 auto 40px

    @media screen and (max-width: 375px)
      margin: 0 auto 20px
</style>
